import { useCallback, useEffect, useReducer, useState } from "react";
import {
  buildMenuSavePayload,
  createRestaurantMenu,
  getRestaurantMenu,
  updateRestaurantMenu,
} from "../api/menu";
import { DEFAULT_MENU_THEME } from "../config/menuThemes";
import { parsePersianNumber } from "../utils/persianNumber";
import { errorMessage } from "../utils/errorMessage";

const normalizeName = (value) => String(value || "").trim().replace(/\s+/g, " ");
const duplicateKey = (value) => normalizeName(value).toLocaleLowerCase("fa-IR");

export const entityKey = (entity) => String(entity?._id || entity?.clientId || "");

const normalizeCategories = (categories) => (Array.isArray(categories) ? categories : []).map((category) => ({
  ...category,
  name: normalizeName(category?.name),
  icon: category?.icon || null,
  iconColor: category?.iconColor || "#F88225",
  items: Array.isArray(category?.items) ? category.items.map((item) => ({ ...item })) : [],
}));

export const createEditorState = (menu = {}) => ({
  title: menu?.title || "",
  theme: menu?.theme || DEFAULT_MENU_THEME,
  categories: normalizeCategories(menu?.categories),
  revision: 0,
  savedRevision: 0,
  lastSavedAt: menu?.updatedAt || null,
  error: null,
});

const changed = (state, values) => ({
  ...state,
  ...values,
  revision: state.revision + 1,
  error: null,
});

const move = (items, activeKey, overKey) => {
  const from = items.findIndex((item) => entityKey(item) === String(activeKey));
  const to = items.findIndex((item) => entityKey(item) === String(overKey));
  if (from < 0 || to < 0 || from === to) return items;
  const reordered = [...items];
  const [active] = reordered.splice(from, 1);
  reordered.splice(to, 0, active);
  return reordered;
};

const updateCategory = (categories, categoryKey, updater) => categories.map((category) => (
  entityKey(category) === String(categoryKey) ? updater(category) : category
));

export const menuEditorReducer = (state, action) => {
  switch (action.type) {
    case "LOAD_MENU":
      return createEditorState(action.menu);
    case "SET_TITLE":
      return action.title === state.title ? state : changed(state, { title: action.title });
    case "SET_THEME":
      return action.theme === state.theme ? state : changed(state, { theme: action.theme });
    case "ADD_CATEGORY": { 
      const name = normalizeName(action.name);
      if (!name) return { ...state, error: "نام دسته‌بندی را وارد کنید." };
      if (state.categories.some((category) => duplicateKey(category.name) === duplicateKey(name))) {
        return { ...state, error: "این دسته‌بندی قبلاً اضافه شده است." };
      }
      return changed(state, {
        categories: [
          ...state.categories,
          {
            clientId: action.clientId,
            name,
            icon: null,
            iconColor: "#F88225",
            items: [],
          },
        ],
      });
    }
    case "RENAME_CATEGORY": {
      const name = normalizeName(action.name);
      if (name && state.categories.some((category) => (
        entityKey(category) !== String(action.categoryKey)
        && duplicateKey(category.name) === duplicateKey(name)
      ))) {
        return { ...state, error: "این دسته‌بندی قبلاً اضافه شده است." };
      }
      const categories = updateCategory(state.categories, action.categoryKey, (category) => (
        category.name === name ? category : { ...category, name }
      ));
      return changed(state, { categories });
    }
    case "SET_CATEGORY_APPEARANCE": {
      const categories = updateCategory(state.categories, action.categoryKey, (category) => ({
        ...category,
        icon: action.icon ?? category.icon,
        iconColor: action.iconColor || category.iconColor,
      }));
      return changed(state, { categories });
    }
    case "DELETE_CATEGORY": {
      const categories = state.categories.filter(
        (category) => entityKey(category) !== String(action.categoryKey)
      );
      return categories.length === state.categories.length ? state : changed(state, { categories });
    }
    case "MOVE_CATEGORY": {
      const categories = move(state.categories, action.activeKey, action.overKey);
      return categories === state.categories ? state : changed(state, { categories });
    }
    case "UPSERT_ITEM": {
      const categories = updateCategory(state.categories, action.categoryKey, (category) => {
        const itemIndex = category.items.findIndex(
          (item) => entityKey(item) === String(action.itemKey || "")
        );
        if (itemIndex < 0) {
          return {
            ...category,
            items: [...category.items, { ...action.item, clientId: action.clientId }],
          };
        }
        const items = [...category.items];
        items[itemIndex] = { ...items[itemIndex], ...action.item };
        return { ...category, items };
      });
      return changed(state, { categories });
    }
    case "DELETE_ITEM": {
      const categories = updateCategory(state.categories, action.categoryKey, (category) => ({
        ...category,
        items: category.items.filter((item) => entityKey(item) !== String(action.itemKey)),
      }));
      return changed(state, { categories });
    }
    case "DUPLICATE_ITEM": {
      const categories = updateCategory(state.categories, action.categoryKey, (category) => {
        const itemIndex = category.items.findIndex(
          (item) => entityKey(item) === String(action.itemKey)
        );
        if (itemIndex < 0) return category;
        const source = category.items[itemIndex];
        const { _id, ...copy } = source;
        const items = [...category.items];
        items.splice(itemIndex + 1, 0, {
          ...copy,
          clientId: action.clientId,
          name: `${normalizeName(source.name)} (کپی)`,
        });
        return { ...category, items };
      });
      return changed(state, { categories });
    }
    case "MOVE_ITEM": {
      const categories = updateCategory(state.categories, action.categoryKey, (category) => ({
        ...category,
        items: move(category.items, action.activeKey, action.overKey),
      }));
      return changed(state, { categories });
    }
    case "MARK_SAVED":
      return {
        ...state,
        savedRevision: state.revision,
        lastSavedAt: action.savedAt,
        error: null,
      };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "CLEAR_ERROR":
      return state.error ? { ...state, error: null } : state;
    default:
      return state;
  }
};

export const editorIsDirty = (state) => state.revision !== state.savedRevision;

export const validateMenuDraft = (state) => {
  if (!normalizeName(state?.title)) return "عنوان منو را وارد کنید.";
  const categories = Array.isArray(state?.categories) ? state.categories : [];
  if (!categories.length || !categories.some((category) => category.items?.length)) {
    return "حداقل یک دسته‌بندی با یک آیتم ایجاد کنید.";
  }
  for (const category of categories) {
    if (!normalizeName(category.name)) return "نام همه دسته‌بندی‌ها را کامل کنید.";
    for (const item of category.items || []) {
      if (!normalizeName(item.name)) return "نام همه آیتم‌ها را کامل کنید.";
      if (parsePersianNumber(item.price) === null) return `قیمت «${item.name}» معتبر نیست.`;
    }
  }
  return null;
};

const createClientId = (prefix) => {
  const randomId = window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${randomId}`;
};

const useMenuEditor = ({ menuId, initialMenu } = {}) => {
  const [state, dispatch] = useReducer(menuEditorReducer, initialMenu || {}, createEditorState);
  const [loading, setLoading] = useState(Boolean(menuId && !initialMenu));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialMenu) {
      dispatch({ type: "LOAD_MENU", menu: initialMenu });
      setLoading(false);
      return undefined;
    }
    if (!menuId) {
      dispatch({ type: "LOAD_MENU", menu: {} });
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    getRestaurantMenu(menuId)
      .then((menu) => {
        if (active) dispatch({ type: "LOAD_MENU", menu });
      })
      .catch((error) => {
        if (active) dispatch({
          type: "SET_ERROR",
          error: errorMessage(error, "دریافت اطلاعات منو ناموفق بود."),
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [initialMenu, menuId]);

  useEffect(() => {
    if (!editorIsDirty(state)) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const warnBeforeLinkNavigation = (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link || link.target === "_blank" || event.defaultPrevented) return;
      if (!window.confirm("تغییرات ذخیره‌نشده دارید. بدون ذخیره خارج می‌شوید؟")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    document.addEventListener("click", warnBeforeLinkNavigation, true);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeUnload);
      document.removeEventListener("click", warnBeforeLinkNavigation, true);
    };
  }, [state]);

  const addCategory = useCallback((name) => dispatch({
    type: "ADD_CATEGORY",
    name,
    clientId: createClientId("category"),
  }), []);

  const upsertItem = useCallback((categoryKey, item, itemKey) => dispatch({
    type: "UPSERT_ITEM",
    categoryKey,
    itemKey,
    item,
    clientId: itemKey ? undefined : createClientId("item"),
  }), []);

  const duplicateItem = useCallback((categoryKey, itemKey) => dispatch({
    type: "DUPLICATE_ITEM",
    categoryKey,
    itemKey,
    clientId: createClientId("item"),
  }), []);

  const save = useCallback(async () => {
    const validationError = validateMenuDraft(state);
    if (validationError) {
      dispatch({ type: "SET_ERROR", error: validationError });
      throw new Error(validationError);
    }

    setSaving(true);
    try {
      const payload = buildMenuSavePayload(state);
      const data = menuId
        ? await updateRestaurantMenu(menuId, payload)
        : await createRestaurantMenu(payload);
      const savedMenu = data?.menu || data;
      if (savedMenu?.categories) dispatch({ type: "LOAD_MENU", menu: savedMenu });
      else dispatch({ type: "MARK_SAVED", savedAt: new Date().toISOString() });
      return savedMenu;
    } catch (error) {
      const message = errorMessage(error, "ذخیره منو ناموفق بود.");
      dispatch({ type: "SET_ERROR", error: message });
      if (error?.message === message) throw error;
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  }, [menuId, state]);

  return {
    state,
    dispatch,
    loading,
    saving,
    isDirty: editorIsDirty(state),
    addCategory,
    upsertItem,
    duplicateItem,
    save,
  };
};

export default useMenuEditor;
